import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useSidebar } from "@/context/SidebarContext";
import {
  ReactFlow, ReactFlowProvider, Background, Controls, MiniMap, Handle, Position,
  addEdge, MarkerType, useNodesState, useEdgesState, useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  ArrowLeft, Loader2, Zap, MessageSquareText, LayoutTemplate, ListChecks, X, Plus, Trash2,
  Image, Link, Clock,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";

const primaryBtn = "bg-orange-600 hover:bg-orange-700 text-white font-semibold rounded-md px-4 py-2 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 text-sm";
const inputCls = "border border-white/20 bg-white/5 text-white placeholder:text-slate-500 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500";

const genId = () => Math.random().toString(36).slice(2, 10);

const ACTIVATION_METHODS = [
  { value: "contains", label: "Partial match", hint: "Fires if the keyword appears anywhere in the message" },
  { value: "exact", label: "Exact match", hint: "Fires only if the message is exactly the keyword" },
  { value: "starts_with", label: "Starts with", hint: "Fires if the message starts with the keyword" },
  { value: "any_message", label: "All messages", hint: "Fires on any incoming message, no keyword needed" },
];

const NODE_DEFAULTS = {
  send_message: { text: "" },
  send_template: { template_name: "", language: "en" },
  button_message: { text: "", buttons: [] },
  media_message: { media_type: "image", media_url: "", caption: "" },
  cta_url: { text: "", button_text: "", url: "" },
  wait_timer: { seconds: 2 },
};

const MEDIA_TYPES = [
  { value: "image", label: "Image" },
  { value: "video", label: "Video" },
  { value: "document", label: "Document" },
];

// ---- Custom node cards ----

function DeleteBtn({ id }) {
  const { deleteElements } = useReactFlow();
  return (
    <button
      onClick={(e) => { e.stopPropagation(); deleteElements({ nodes: [{ id }] }); }}
      className="text-white/80 hover:text-white shrink-0"
      data-testid={`delete-node-${id}`}
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );
}

function NodeIconBadge({ icon: Icon, className }) {
  return (
    <span className={`h-6 w-6 rounded-md bg-white/15 flex items-center justify-center shrink-0 ${className || ""}`}>
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

function TriggerNode({ id, data }) {
  const method = ACTIVATION_METHODS.find((m) => m.value === data.triggerType) || ACTIVATION_METHODS[0];
  const isAnyMessage = method.value === "any_message";
  const hasKeywords = !!data.keywordsPreview;
  return (
    <div className="w-[240px] rounded-xl border border-orange-500/30 bg-[#141414] shadow-lg overflow-hidden" data-testid={`flow-node-${id}`}>
      <div className="flex items-center gap-2.5 px-3.5 py-3 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <span className="h-8 w-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
          <Zap className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <p className="font-heading font-bold text-sm leading-none">Automation</p>
          <span className="inline-block mt-1.5 text-[9px] font-bold uppercase tracking-wide bg-white/20 rounded-full px-2 py-0.5">
            Activation root
          </span>
        </div>
      </div>
      <div className="px-3.5 py-3">
        <p className="text-xs font-semibold text-white">
          Customer <span className="text-slate-600">·</span> {method.label}
        </p>
        {isAnyMessage ? (
          <p className="text-[11px] text-slate-400 mt-1">Fires on any incoming message</p>
        ) : hasKeywords ? (
          <p className="text-[11px] text-slate-400 truncate mt-1">{data.keywordsPreview}</p>
        ) : (
          <p className="text-[11px] text-slate-500 italic mt-1">click to set a keyword</p>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-orange-600 !w-3 !h-3 !cursor-pointer"
             onClick={(e) => data.onAddClick(e, id, null)} />
    </div>
  );
}

function SendMessageNode({ id, data }) {
  return (
    <div className="w-[220px] rounded-xl border border-white/10 bg-[#141414] shadow-lg overflow-hidden" data-testid={`flow-node-${id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5" />
      <div className="flex items-center justify-between gap-1.5 px-3 py-2.5 bg-sky-600 text-white">
        <span className="flex items-center gap-2 truncate">
          <NodeIconBadge icon={MessageSquareText} />
          <span className="text-xs font-bold tracking-wide">SEND MESSAGE</span>
        </span>
        <DeleteBtn id={id} />
      </div>
      <div className="px-3 py-2.5 text-xs text-slate-400 line-clamp-3 min-h-[2.5rem]">
        {data.text || <span className="text-slate-500 italic">click to add message text</span>}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-sky-600 !w-3 !h-3 !cursor-pointer"
             onClick={(e) => data.onAddClick(e, id, null)} />
    </div>
  );
}

function SendTemplateNode({ id, data }) {
  return (
    <div className="w-[220px] rounded-xl border border-white/10 bg-[#141414] shadow-lg overflow-hidden" data-testid={`flow-node-${id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5" />
      <div className="flex items-center justify-between gap-1.5 px-3 py-2.5 bg-emerald-600 text-white">
        <span className="flex items-center gap-2 truncate">
          <NodeIconBadge icon={LayoutTemplate} />
          <span className="text-xs font-bold tracking-wide">SEND TEMPLATE</span>
        </span>
        <DeleteBtn id={id} />
      </div>
      <div className="px-3 py-2.5 text-xs text-slate-400 min-h-[2.5rem]">
        {data.template_name || <span className="text-slate-500 italic">click to pick a template</span>}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-emerald-600 !w-3 !h-3 !cursor-pointer"
             onClick={(e) => data.onAddClick(e, id, null)} />
    </div>
  );
}

function ButtonMessageNode({ id, data }) {
  const buttons = data.buttons || [];
  return (
    <div className="w-[240px] rounded-xl border border-white/10 bg-[#141414] shadow-lg overflow-hidden" data-testid={`flow-node-${id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5" />
      <div className="flex items-center justify-between gap-1.5 px-3 py-2.5 bg-amber-500 text-white">
        <span className="flex items-center gap-2 truncate">
          <NodeIconBadge icon={ListChecks} />
          <span className="text-xs font-bold tracking-wide">BUTTON MESSAGE</span>
        </span>
        <DeleteBtn id={id} />
      </div>
      <div className="px-3 py-2 text-xs text-slate-400 line-clamp-2 border-b border-white/10 min-h-[2rem]">
        {data.text || <span className="text-slate-500 italic">click to add message + buttons</span>}
      </div>
      {buttons.length === 0 ? (
        <p className="px-3 py-2 text-xs text-slate-500 italic">no buttons yet</p>
      ) : buttons.map((b) => (
        <div key={b.id} className="relative px-3 py-1.5 text-xs font-semibold text-slate-300 border-b last:border-b-0 border-white/10 truncate">
          {b.title || <span className="text-slate-500 italic font-normal">untitled</span>}
          <Handle type="source" position={Position.Right} id={b.id} className="!bg-amber-500 !w-3 !h-3 !cursor-pointer"
                 onClick={(e) => data.onAddClick(e, id, b.id)} />
        </div>
      ))}
    </div>
  );
}

function MediaMessageNode({ id, data }) {
  return (
    <div className="w-[220px] rounded-xl border border-white/10 bg-[#141414] shadow-lg overflow-hidden" data-testid={`flow-node-${id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5" />
      <div className="flex items-center justify-between gap-1.5 px-3 py-2.5 bg-violet-600 text-white">
        <span className="flex items-center gap-2 truncate">
          <NodeIconBadge icon={Image} />
          <span className="text-xs font-bold tracking-wide">SEND MEDIA</span>
        </span>
        <DeleteBtn id={id} />
      </div>
      <div className="px-3 py-2.5 text-xs text-slate-400 min-h-[2.5rem]">
        {data.media_url ? (
          <span className="truncate block">{data.media_type || "image"} attached</span>
        ) : (
          <span className="text-slate-500 italic">click to add an image/video/document link</span>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-violet-600 !w-3 !h-3 !cursor-pointer"
             onClick={(e) => data.onAddClick(e, id, null)} />
    </div>
  );
}

function CtaUrlNode({ id, data }) {
  return (
    <div className="w-[220px] rounded-xl border border-white/10 bg-[#141414] shadow-lg overflow-hidden" data-testid={`flow-node-${id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5" />
      <div className="flex items-center justify-between gap-1.5 px-3 py-2.5 bg-teal-600 text-white">
        <span className="flex items-center gap-2 truncate">
          <NodeIconBadge icon={Link} />
          <span className="text-xs font-bold tracking-wide">CALL TO ACTION</span>
        </span>
        <DeleteBtn id={id} />
      </div>
      <div className="px-3 py-2.5 text-xs text-slate-400 line-clamp-2 min-h-[2.5rem]">
        {data.button_text ? (
          <span>Button: <span className="text-slate-300 font-semibold">{data.button_text}</span></span>
        ) : (
          <span className="text-slate-500 italic">click to set the link button</span>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-teal-600 !w-3 !h-3 !cursor-pointer"
             onClick={(e) => data.onAddClick(e, id, null)} />
    </div>
  );
}

function WaitTimerNode({ id, data }) {
  return (
    <div className="w-[200px] rounded-xl border border-white/10 bg-[#141414] shadow-lg overflow-hidden" data-testid={`flow-node-${id}`}>
      <Handle type="target" position={Position.Left} className="!bg-slate-400 !w-2.5 !h-2.5" />
      <div className="flex items-center justify-between gap-1.5 px-3 py-2.5 bg-orange-500 text-white">
        <span className="flex items-center gap-2 truncate">
          <NodeIconBadge icon={Clock} />
          <span className="text-xs font-bold tracking-wide">WAIT TIMER</span>
        </span>
        <DeleteBtn id={id} />
      </div>
      <div className="px-3 py-2.5 text-xs text-slate-400 min-h-[2.5rem]">
        Wait {data.seconds || 2} second{(data.seconds || 2) === 1 ? "" : "s"}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-orange-500 !w-3 !h-3 !cursor-pointer"
             onClick={(e) => data.onAddClick(e, id, null)} />
    </div>
  );
}

const nodeTypes = {
  trigger: TriggerNode,
  send_message: SendMessageNode,
  send_template: SendTemplateNode,
  button_message: ButtonMessageNode,
  media_message: MediaMessageNode,
  cta_url: CtaUrlNode,
  wait_timer: WaitTimerNode,
};

const PALETTE = [
  {
    category: "Messaging",
    items: [
      { type: "send_message", label: "Send Message", icon: MessageSquareText, color: "bg-sky-600" },
      { type: "send_template", label: "Send Template", icon: LayoutTemplate, color: "bg-emerald-600" },
      { type: "button_message", label: "Button Message", icon: ListChecks, color: "bg-amber-500" },
      { type: "media_message", label: "Send Media", icon: Image, color: "bg-violet-600" },
      { type: "cta_url", label: "Call to Action", icon: Link, color: "bg-teal-600" },
      { type: "wait_timer", label: "Wait Timer", icon: Clock, color: "bg-orange-500" },
    ],
  },
];

// ---- Quick "add next step" popup (opens on click, or on dropping a dragged connector on empty canvas) ----

function AddStepMenu({ menu, onPick, onClose }) {
  if (!menu) return null;
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="fixed z-50 w-48 rounded-xl border border-white/10 bg-[#141414] shadow-2xl overflow-hidden"
        style={{ left: menu.screenX + 14, top: menu.screenY - 10 }}
      >
        <p className="px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 border-b border-white/10">
          Add next step
        </p>
        <div className="max-h-72 overflow-y-auto">
          {PALETTE.flatMap((g) => g.items).map((p) => (
            <button
              key={p.type}
              data-testid={`add-step-${p.type}`}
              onClick={() => onPick(p.type)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs font-semibold text-slate-300 hover:bg-white/10"
            >
              <span className={`h-6 w-6 rounded-md flex items-center justify-center text-white shrink-0 ${p.color}`}>
                <p.icon className="h-3.5 w-3.5" />
              </span>
              {p.label}
            </button>
          ))}
        </div>
        <button onClick={onClose} className="w-full px-3 py-2 text-xs font-semibold text-red-400 hover:bg-red-500/10 border-t border-white/10">
          Cancel
        </button>
      </div>
    </>
  );
}

// ---- Editor sheet (per-node config) ----

function NodeEditorSheet({ node, templates, onChange, onClose }) {
  if (!node) return null;
  const data = node.data || {};

  const addButton = () => {
    if ((data.buttons || []).length >= 3) return;
    onChange(node.id, { buttons: [...(data.buttons || []), { id: genId(), title: "" }] });
  };
  const updateButton = (idx, title) => {
    const buttons = [...(data.buttons || [])];
    buttons[idx] = { ...buttons[idx], title };
    onChange(node.id, { buttons });
  };
  const removeButton = (idx) => {
    const buttons = (data.buttons || []).filter((_, i) => i !== idx);
    onChange(node.id, { buttons });
  };

  return (
    <Sheet open={!!node} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="node-editor-sheet" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-heading">
            {node.type === "send_message" && "Send Message"}
            {node.type === "send_template" && "Send Template"}
            {node.type === "button_message" && "Button Message"}
            {node.type === "media_message" && "Send Media"}
            {node.type === "cta_url" && "Call to Action"}
            {node.type === "wait_timer" && "Wait Timer"}
          </SheetTitle>
        </SheetHeader>

        {node.type === "send_message" && (
          <div className="mt-4">
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Message Text</label>
            <Textarea data-testid="node-text-input" rows={5} className="mt-1.5" value={data.text || ""}
                     onChange={(e) => onChange(node.id, { text: e.target.value })} placeholder="Type what the bot should send…" />
          </div>
        )}

        {node.type === "send_template" && (
          <div className="mt-4">
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Approved Template</label>
            <Select value={data.template_name || ""} onValueChange={(v) => {
              const t = templates.find((x) => x.name === v);
              onChange(node.id, { template_name: v, language: t?.language || "en" });
            }}>
              <SelectTrigger data-testid="node-template-select" className="mt-1.5"><SelectValue placeholder="Pick an approved template" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} ({t.language})</SelectItem>)}</SelectContent>
            </Select>
            {templates.length === 0 && <p className="text-xs text-slate-500 mt-1">No approved templates on this branch.</p>}
          </div>
        )}

        {node.type === "button_message" && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Message Text</label>
              <Textarea data-testid="node-text-input" rows={3} className="mt-1.5" value={data.text || ""}
                       onChange={(e) => onChange(node.id, { text: e.target.value })} placeholder="Type the message shown above the buttons…" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Buttons (max 3)</label>
                {(data.buttons || []).length < 3 && (
                  <button data-testid="add-button-btn" onClick={addButton} className="text-orange-500 hover:text-orange-400 text-xs font-bold flex items-center gap-1">
                    <Plus className="h-3.5 w-3.5" /> Add
                  </button>
                )}
              </div>
              <div className="mt-2 space-y-2">
                {(data.buttons || []).length === 0 && <p className="text-xs text-slate-500 italic">No buttons yet. Add one, then drag a line from it to the next block.</p>}
                {(data.buttons || []).map((b, idx) => (
                  <div key={b.id} className="flex items-center gap-2">
                    <input data-testid={`button-title-input-${idx}`} className={`flex-1 ${inputCls}`} value={b.title}
                          maxLength={20} onChange={(e) => updateButton(idx, e.target.value)} placeholder={`Button ${idx + 1} text`} />
                    <button data-testid={`remove-button-btn-${idx}`} onClick={() => removeButton(idx)} className="p-1.5 rounded-md text-red-500 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {node.type === "media_message" && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Media Type</label>
              <Select value={data.media_type || "image"} onValueChange={(v) => onChange(node.id, { media_type: v })}>
                <SelectTrigger data-testid="node-media-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{MEDIA_TYPES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Media URL</label>
              <input data-testid="node-media-url-input" className={`mt-1.5 w-full ${inputCls}`} value={data.media_url || ""}
                    onChange={(e) => onChange(node.id, { media_url: e.target.value })} placeholder="https://…" />
              <p className="text-[11px] text-slate-500 mt-1">Must be a public, direct file link (not a Google Drive share link).</p>
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Caption (optional)</label>
              <Textarea data-testid="node-caption-input" rows={2} className="mt-1.5" value={data.caption || ""}
                       onChange={(e) => onChange(node.id, { caption: e.target.value })} placeholder="Shown under the media…" />
            </div>
          </div>
        )}

        {node.type === "cta_url" && (
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Message Text</label>
              <Textarea data-testid="node-text-input" rows={3} className="mt-1.5" value={data.text || ""}
                       onChange={(e) => onChange(node.id, { text: e.target.value })} placeholder="Shown above the button…" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Button Text</label>
              <input data-testid="node-button-text-input" className={`mt-1.5 w-full ${inputCls}`} value={data.button_text || ""}
                    maxLength={20} onChange={(e) => onChange(node.id, { button_text: e.target.value })} placeholder="Visit Our Site" />
            </div>
            <div>
              <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Link URL</label>
              <input data-testid="node-url-input" className={`mt-1.5 w-full ${inputCls}`} value={data.url || ""}
                    onChange={(e) => onChange(node.id, { url: e.target.value })} placeholder="https://…" />
            </div>
          </div>
        )}

        {node.type === "wait_timer" && (
          <div className="mt-4">
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Wait (seconds)</label>
            <input data-testid="node-seconds-input" type="number" min={1} max={15} className={`mt-1.5 w-full ${inputCls}`}
                  value={data.seconds || 2}
                  onChange={(e) => onChange(node.id, { seconds: Math.max(1, Math.min(15, Number(e.target.value) || 1)) })} />
            <p className="text-[11px] text-slate-500 mt-1">Capped at 15 seconds — the flow runs while WhatsApp is waiting for a reply, so a long wait risks the message being marked as failed.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---- Trigger sheet (Configure Start Trigger) ----

function TriggerEditorSheet({ open, triggerType, onTriggerTypeChange, keywordsInput, onKeywordsChange, onClose }) {
  const isAnyMessage = triggerType === "any_message";
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="trigger-editor-sheet" className="w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-heading">Configure Start Trigger</SheetTitle>
        </SheetHeader>

        <div className="mt-4">
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Audience</label>
          <div className={`mt-1.5 ${inputCls} w-full text-slate-400`}>Customer</div>
        </div>

        <div className="mt-4">
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Activation method</label>
          <p className="text-[11px] text-slate-500 mt-1 mb-2">How incoming messages should be matched.</p>
          <div className="grid grid-cols-2 gap-2">
            {ACTIVATION_METHODS.map((m) => (
              <button key={m.value} data-testid={`trigger-method-${m.value}`} onClick={() => onTriggerTypeChange(m.value)}
                     className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${
                       triggerType === m.value
                         ? "border-orange-500 bg-orange-500/10 text-white"
                         : "border-white/10 bg-white/5 text-slate-400 hover:border-white/20"
                     }`}>
                <p className="text-xs font-bold">{m.label}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 leading-snug">{m.hint}</p>
              </button>
            ))}
          </div>
        </div>

        {!isAnyMessage && (
          <div className="mt-4">
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400">Keywords</label>
            <input data-testid="trigger-keywords-input" value={keywordsInput} onChange={(e) => onKeywordsChange(e.target.value)}
                  placeholder="hi, hello, timings" className={`mt-1.5 ${inputCls} w-full`} />
            <p className="text-[11px] text-slate-500 mt-1">Comma-separated. Not case-sensitive.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---- Main canvas ----

function FlowCanvasInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeBranchId } = useBranch();
  const { screenToFlowPosition } = useReactFlow();
  const { collapsed } = useSidebar();
  const sideOffset = collapsed ? "left-[72px]" : "left-64";

  const [loading, setLoading] = useState(true);
  const [nameInput, setNameInput] = useState("");
  const [triggerType, setTriggerType] = useState("contains");
  const [keywordsInput, setKeywordsInput] = useState("");
  const [activeToggle, setActiveToggle] = useState(false);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [triggerSheetOpen, setTriggerSheetOpen] = useState(false);
  const [addMenu, setAddMenu] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/flows/${id}`);
        setNameInput(data.name || "");
        setTriggerType(data.trigger_type || "contains");
        setKeywordsInput((data.trigger_keywords || []).join(", "));
        setActiveToggle(!!data.is_active);
        setNodes(data.nodes || []);
        setEdges((data.edges || []).map((e) => ({
          ...e, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed, color: "#64748b" },
        })));
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        navigate("/flows");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!activeBranchId) return;
    (async () => {
      const { data } = await api.get("/templates", { params: { branch_id: activeBranchId } });
      setTemplates(data.filter((t) => (t.status || "").toUpperCase() === "APPROVED"));
    })();
  }, [activeBranchId]);

  const openAddMenu = useCallback((event, sourceId, sourceHandle) => {
    event.stopPropagation();
    const flowPosition = screenToFlowPosition({ x: event.clientX + 60, y: event.clientY - 20 });
    setAddMenu({ sourceId, sourceHandle: sourceHandle || null, screenX: event.clientX, screenY: event.clientY, flowPosition });
  }, [screenToFlowPosition]);

  const pickAddStep = useCallback((type) => {
    if (!addMenu || !NODE_DEFAULTS[type]) return;
    const newId = `${type}-${genId()}`;
    setNodes((ns) => ns.concat({ id: newId, type, position: addMenu.flowPosition, data: { ...NODE_DEFAULTS[type] } }));
    setEdges((eds) => {
      const filtered = eds.filter((e) => !(e.source === addMenu.sourceId && e.sourceHandle === addMenu.sourceHandle));
      return addEdge({
        source: addMenu.sourceId, sourceHandle: addMenu.sourceHandle, target: newId,
        type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed, color: "#64748b" },
      }, filtered);
    });
    setAddMenu(null);
  }, [addMenu, setNodes, setEdges]);

  const onConnectEnd = useCallback((event, connectionState) => {
    if (connectionState.isValid) return; // dropped on a real handle -- onConnect already wired it
    const point = event.changedTouches ? event.changedTouches[0] : event;
    const flowPosition = screenToFlowPosition({ x: point.clientX + 60, y: point.clientY - 20 });
    setAddMenu({
      sourceId: connectionState.fromNode?.id,
      sourceHandle: connectionState.fromHandle?.id || null,
      screenX: point.clientX, screenY: point.clientY, flowPosition,
    });
  }, [screenToFlowPosition]);

  const displayNodes = useMemo(
    () => nodes.map((n) => ({
      ...n,
      deletable: n.type !== "trigger",
      data: {
        ...n.data,
        ...(n.type === "trigger" ? { keywordsPreview: keywordsInput, triggerType } : {}),
        onAddClick: openAddMenu,
      },
    })),
    [nodes, keywordsInput, triggerType, openAddMenu]
  );

  const updateNodeData = useCallback((nodeId, patch) => {
    setNodes((ns) => ns.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)));
  }, [setNodes]);

  const onConnect = useCallback((connection) => {
    setEdges((eds) => {
      const filtered = eds.filter((e) => !(e.source === connection.source && e.sourceHandle === connection.sourceHandle));
      return addEdge({ ...connection, type: "smoothstep", markerEnd: { type: MarkerType.ArrowClosed, color: "#64748b" } }, filtered);
    });
  }, [setEdges]);

  const onNodeClick = useCallback((_event, node) => {
    if (node.type === "trigger") {
      setTriggerSheetOpen(true);
      return;
    }
    setEditingNodeId(node.id);
  }, []);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData("application/reactflow");
    if (!type || !NODE_DEFAULTS[type]) return;
    const position = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const newId = `${type}-${genId()}`;
    setNodes((ns) => ns.concat({ id: newId, type, position, data: { ...NODE_DEFAULTS[type] } }));
  }, [screenToFlowPosition, setNodes]);

  const onDragStart = (event, type) => {
    event.dataTransfer.setData("application/reactflow", type);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleSave = async (nextActive = activeToggle) => {
    setSaving(true);
    try {
      const cleanNodes = nodes.map(({ id: nid, type, position, data }) => ({ id: nid, type, position, data }));
      const cleanEdges = edges.map(({ id: eid, source, target, sourceHandle }) => ({ id: eid, source, target, sourceHandle: sourceHandle || null }));
      const payload = {
        name: nameInput.trim() || "Untitled Flow",
        trigger_type: triggerType,
        trigger_keywords: keywordsInput.split(",").map((k) => k.trim()).filter(Boolean),
        nodes: cleanNodes,
        edges: cleanEdges,
        is_active: nextActive,
      };
      const { data } = await api.put(`/flows/${id}`, payload);
      setActiveToggle(!!data.is_active);
      toast.success("Flow saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setActiveToggle(!nextActive);
    } finally {
      setSaving(false);
    }
  };

  const editingNode = nodes.find((n) => n.id === editingNodeId) || null;

  if (loading) {
    return <div className={`fixed inset-0 ${sideOffset} top-16 flex items-center justify-center bg-[#0a0a0a] transition-[left] duration-300 ease-in-out`}><Loader2 className="h-6 w-6 animate-spin text-orange-600" /></div>;
  }

  return (
    <>
      <div className={`fixed top-16 ${sideOffset} right-0 h-16 z-30 bg-[#0a0a0a] border-b border-white/10 flex items-center gap-3 px-6 transition-[left] duration-300 ease-in-out`}>
        <button data-testid="back-to-flows-button" onClick={() => navigate("/flows")} className="p-2 rounded-md hover:bg-white/10 text-slate-400">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <input data-testid="flow-name-field" value={nameInput} onChange={(e) => setNameInput(e.target.value)}
              className="font-heading font-bold text-base text-white border-none focus:outline-none focus:ring-0 bg-transparent w-48" />
        <div className="flex-1" />
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-300">
          <Switch data-testid="flow-active-toggle" checked={activeToggle} onCheckedChange={(v) => { setActiveToggle(v); handleSave(v); }} />
          {activeToggle ? "On" : "Off"}
        </label>
        <button data-testid="save-flow-button" onClick={() => handleSave()} disabled={saving} className={primaryBtn}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
        </button>
      </div>

      <div className={`fixed top-32 ${sideOffset} right-0 bottom-0 flex bg-[#0d0d0d] transition-[left] duration-300 ease-in-out`}>
        <div className="relative flex-1 h-full" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={displayNodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            deleteKeyCode={["Backspace", "Delete"]}
          >
            <Background gap={16} color="#262626" />
            <Controls />
            <MiniMap pannable zoomable className="!bg-[#141414]" />
          </ReactFlow>
        </div>

        <aside className="w-64 h-full border-l border-white/10 bg-[#0a0a0a] overflow-y-auto p-4 shrink-0">
          <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-400 mb-4">Drag a block onto the canvas</p>
          <div className="space-y-5">
            {PALETTE.map((group) => (
              <div key={group.category}>
                <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 mb-2">{group.category}</p>
                <div className="grid grid-cols-2 gap-2">
                  {group.items.map((p) => (
                    <div key={p.type} draggable data-testid={`palette-${p.type}`}
                        onDragStart={(e) => onDragStart(e, p.type)}
                        className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-lg border border-white/10 bg-[#141414] hover:border-orange-500/50 hover:bg-orange-500/10 hover:-translate-y-0.5 cursor-grab active:cursor-grabbing transition-all duration-150 text-center">
                      <span className={`h-8 w-8 rounded-lg flex items-center justify-center text-white shrink-0 ${p.color}`}><p.icon className="h-4 w-4" /></span>
                      <span className="text-xs font-semibold text-slate-300 leading-tight">{p.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed mt-5">
            Connect blocks by dragging a line from the dot on one block's right edge to the next block's left edge.
          </p>
        </aside>
      </div>

      <NodeEditorSheet node={editingNode} templates={templates} onChange={updateNodeData} onClose={() => setEditingNodeId(null)} />
      <TriggerEditorSheet open={triggerSheetOpen} triggerType={triggerType} onTriggerTypeChange={setTriggerType}
                         keywordsInput={keywordsInput} onKeywordsChange={setKeywordsInput} onClose={() => setTriggerSheetOpen(false)} />
      <AddStepMenu menu={addMenu} onPick={pickAddStep} onClose={() => setAddMenu(null)} />
    </>
  );
}

export default function FlowEditor() {
  return (
    <ReactFlowProvider>
      <FlowCanvasInner />
    </ReactFlowProvider>
  );
}
